import { Button, IconButton, Modal, Stack, TextField, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { createRootStyle } from '../styles';
import PublicAPI from '@/api/TreedisPublicDAO';
import { User } from '@/_models/User';
import useSSOLogin from '@/hooks/useSSOLogin';
import getTokenData from '@/utils/getTokenData';
import { useNotesContext } from '../../hooks/useNotesContext';
import { FAIcon } from '@packages/ui-components';

const getReCaptchaToken = () => {
  return new Promise((resolve, reject) => {
    if (!process.env.NEXT_PUBLIC_RECAPTCHA_KEY) resolve(null);
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.NEXT_PUBLIC_RECAPTCHA_KEY, { action: 'main' })
        .then(resolve)
        .catch(reject);
    });
  });
};

export const ThreadLoginPopup = () => {
  const [isOpen, setIsOpen] = useState(!localStorage.getItem('publicUserToken'));
  const [inputs, setInputs] = useState({ email: '', password: '', pinTwoFA: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);

  const { collaborators, tour, setIsGuest, setUserId, userId, shouldShowCollaboratorsLogin } =
    useNotesContext();

  const shouldUseSSO = window.location.hostname === 'icl.treedis.com';
  const { enabledSSO, handleSSOLogin } = shouldUseSSO
    ? useSSOLogin({
        onLoginSuccess: (newUserId) => {
          setUserId(newUserId);
          setIsOpen(false);
        },
      })
    : {
        enabledSSO: false,
        handleSSOLogin: () => {},
      };

  const handleInputsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;

    setInputs((prevState) => ({
      ...prevState,
      [id]: id === 'pinTwoFA' ? value.replace(/\s/g, '') : value,
    }));
  };

  const handleSubmit = async () => {
    if (!inputs.email || !inputs.password) {
      toast.error('Please fill in credentials');
      return;
    }

    // If we're on step 2 (2FA PIN entry), validate that PIN is entered
    if (step === 2 && (!inputs.pinTwoFA || inputs.pinTwoFA.trim() === '')) {
      toast.error('Please enter 2FA PIN');
      return;
    }

    if (!collaborators?.find((c) => c.email === inputs.email)) {
      toast.error('You are not a collaborator on this space');
      return;
    }

    const captchaToken = await getReCaptchaToken();
    const res = tour?.enableIot
      ? await PublicAPI.loginIoT({ ...inputs, captchaToken })
      : await PublicAPI.login({ ...inputs, captchaToken });

    if (res) {
      const { data } = res;
      if (data?.accessToken) {
        localStorage.setItem('publicUserToken', data.accessToken);
        setUserId(Number(data.user.id));
        setIsOpen(false);

        // Dispatch custom event to notify other components about token update
        window.dispatchEvent(new Event('publicUserTokenUpdated'));
      } else if (tour?.enableIot && tour?.iot2FAEnabled) {
        setStep(2);
      } else {
        toast.error(res.message || 'Login failed');
      }
    } else if (!res && step === 2) {
      toast.error('Wrong pin');
    } else {
      toast.error('Wrong password or email');
    }
  };

  useEffect(() => {
    const getSlugFromUrl = () => {
      const segments = window.location.href.split('/');
      return segments[segments.length - 1].split('?')[0];
    };

    const slug = getSlugFromUrl();

    if (tour?.slug && slug !== tour.slug) {
      const token = localStorage.getItem('publicUserToken');
      const { userIdFromToken } = getTokenData(token);

      if (tour?.isPublic === true) {
        setIsOpen(false);
        return;
      }

      if (!shouldShowCollaboratorsLogin) {
        setIsOpen(false);
        return;
      }

      if (!collaborators.map((c) => c.id).includes(Number(userIdFromToken))) {
        setIsOpen(true);
      }
    }
  }, [
    window.location.href,
    tour?.slug,
    tour?.isPublic,
    collaborators,
    shouldShowCollaboratorsLogin,
  ]);

  useEffect(() => {
    const token = localStorage.getItem('publicUserToken');
    const { tokenExpired, userIdFromToken } = getTokenData(token);

    if (tour?.isPublic === true) {
      setIsOpen(false);
      if (!userIdFromToken) {
        setUserId(null);
      } else {
        setUserId(Number(userIdFromToken));
      }
      return;
    }

    if (!shouldShowCollaboratorsLogin) {
      setIsOpen(false);
      if (!userIdFromToken) {
        setUserId(null);
      } else {
        setUserId(Number(userIdFromToken));
      }
      return;
    }

    if ((tokenExpired || !token) && !userId) {
      setIsOpen(true);
    } else if (!collaborators.map((c) => c.id).includes(Number(userIdFromToken))) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }

    if (!userIdFromToken) {
      setUserId(null);
    } else {
      setUserId(Number(userIdFromToken));
    }
  }, [userId, setUserId, collaborators, tour?.isPublic, shouldShowCollaboratorsLogin]);

  const handleEnterPress = (event) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Modal
      open={isOpen}
      hideBackdrop
      sx={{
        background: 'rgba(0, 0, 0, 0.4)',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(15px)',
        zIndex: 1300,
      }}
      onKeyDown={handleEnterPress}
    >
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        gap={{ xs: 2, md: 3 }}
        sx={createRootStyle()}
      >
        {step === 1 ? (
          <>
            <TextField
              required
              autoFocus
              id="email"
              type="email"
              label="Email"
              value={inputs.email}
              onChange={handleInputsChange}
              sx={{
                '& .MuiInputBase-root , input': {
                  height: { xs: '36px', md: '56px' },
                  lineHeight: { xs: '36px', md: '56px' },
                },
              }}
              inputProps={{
                style: {
                  width: '320px',
                  borderColor: '#FFFFFF',
                  borderRadius: '4px',
                  color: 'white',
                },
              }}
              InputLabelProps={{
                sx: { top: { xs: '-10px', md: 0 }, '&.MuiInputLabel-shrink': { top: 0 } },
              }}
            />
            <TextField
              required
              id="password"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={inputs.password}
              onChange={handleInputsChange}
              sx={{
                '& .MuiInputBase-root , input': {
                  height: { xs: '36px', md: '56px' },
                  lineHeight: { xs: '36px', md: '56px' },
                  width: '320px',
                },
              }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword((prevState) => !prevState)}
                    sx={{ color: '#FFF' }}
                  >
                    {showPassword ? <FAIcon icon="faEye" /> : <FAIcon icon="faEyeSlash" />}
                  </IconButton>
                ),
              }}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              inputProps={{
                style: {
                  borderColor: '#FFFFFF',
                  borderRadius: '4px',
                  color: 'white',
                },
              }}
              InputLabelProps={{
                sx: { top: { xs: '-10px', md: 0 }, '&.MuiInputLabel-shrink': { top: 0 } },
              }}
            />
          </>
        ) : (
          <TextField
            required
            autoFocus
            id="pinTwoFA"
            type="twoFactorCode"
            label="Pin Code"
            value={inputs.pinTwoFA}
            onChange={handleInputsChange}
            sx={{
              '& .MuiInputBase-root , input': {
                height: { xs: '36px', md: '56px' },
                lineHeight: { xs: '36px', md: '56px' },
              },
            }}
            inputProps={{
              style: {
                width: '320px',
                borderColor: '#FFFFFF',
                borderRadius: '4px',
                color: 'white',
              },
            }}
            InputLabelProps={{
              sx: { top: { xs: '-10px', md: 0 }, '&.MuiInputLabel-shrink': { top: 0 } },
            }}
          />
        )}
        <Stack gap={1} alignItems="center" width="320px">
          <Button
            type="submit"
            onClick={handleSubmit}
            sx={{
              backgroundColor: '#2055FF',
              my: { xs: 0, lg: '5px' },
              height: '56px',
              width: { xs: '100%' },
            }}
          >
            Login as Collaborator
          </Button>
          {tour?.guestNotesAccess && (
            <Button
              onClick={() => {
                setIsOpen(false);
                setIsGuest(true);
                localStorage.setItem('treedisGuest', JSON.stringify({ isGuest: true }));
              }}
              sx={{
                backgroundColor: '#2055FF',
                my: { xs: 0, lg: '5px' },
                height: '56px',
                width: { xs: '100%' },
              }}
            >
              Enter as Guest
            </Button>
          )}
          {enabledSSO && (
            <Button
              type="submit"
              onClick={handleSSOLogin}
              sx={{
                backgroundColor: '#2055FF',
                my: { xs: 0, lg: '5px' },
                height: '56px',
                width: { xs: '100%' },
              }}
            >
              Login SSO
            </Button>
          )}
          <Typography variant="caption" color="#fff">
            Only Collaborators can add Notes and leave comments. If you don't have access as a
            Collaborator on this space, please contact the admin.
          </Typography>
        </Stack>
      </Stack>
    </Modal>
  );
};
